import MultiDecimalHelper from "@/utils/multi-decimal"
import subCancelReasonCodeConfig from "@/utils/sub-cancel-reason-code-config"
import { PRICING_MODEL } from "@/utils/pricing-model-constants"
import HostedPageHelper from "@/utils/hosted-page-helper"
import {
  ItemType,
  PLAN_LINE_ENTITY_TYPES,
  ADDON_LINE_ENTITY_TYPES,
} from "@/utils/item-type-constants"

export default class PortalHelper {
  static constructOrderItemFromSubData(subData) {
    let output = JSON.parse(JSON.stringify(subData.orderItem));
    return output;
  }

  static cloneAddons(addons) {
    return JSON.parse(JSON.stringify(addons || []));
  }

  static clonePlan(plan) {
    return plan ? JSON.parse(JSON.stringify(plan)) : plan;
  }

  static syncCartAmountsFromSubscriptionEstimate(orderItem, subscriptionEstimate) {
    if (!orderItem || !subscriptionEstimate?.line_items?.length) {
      return orderItem;
    }

    const lineItems = subscriptionEstimate.line_items;

    const syncAmounts = (cartEntity, lineItem) => {
      if (!cartEntity || !lineItem) return;
      cartEntity.amount = lineItem.amount;
      if (lineItem.amount_in_decimal != null) {
        cartEntity.amount_in_decimal = lineItem.amount_in_decimal;
      }
      if (lineItem.unit_amount != null) {
        cartEntity.unit_amount = lineItem.unit_amount;
      }
      if (lineItem.unit_amount_in_decimal != null) {
        cartEntity.unit_amount_in_decimal = lineItem.unit_amount_in_decimal;
      }
    };

    const planLineItem = lineItems.find(
      li => PLAN_LINE_ENTITY_TYPES.has(li.entity_type) && li.entity_id === orderItem.plan?.id
    );
    syncAmounts(orderItem.plan, planLineItem);

    (orderItem.addons || []).forEach(addon => {
      const addonLineItem = lineItems.find(
        li => li.entity_id === addon.id && ADDON_LINE_ENTITY_TYPES.has(li.entity_type)
      );
      syncAmounts(addon, addonLineItem);
    });

    return orderItem;
  }

  static getDetailsFromCart(cart) {
    return {
      planDetail: cart.orderItems[0].plan.details,
      addonDetails: cart.orderItems[0].addons.map(({details}) => details)
    }
  }

  static displayPeriod(days, trial_unit) {
    return `${days} day${days > 1 ? 's' : ''}`;
  }

  static getAdditionalPlanSignature(orderItem) {
    const additionalPlans = (orderItem && orderItem.additionalPlans) || [];
    return JSON.stringify(
      additionalPlans
        .map(plan => ({
          id: plan.id,
          quantity: plan.quantity || 1,
          quantity_in_decimal: plan.quantity_in_decimal
        }))
        .sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0))
    );
  }

  static hasAdditionalPlanChanges(oldOrderItem, changedOrderItem) {
    const oldHasAdditional = !!(oldOrderItem && oldOrderItem.additionalPlans && oldOrderItem.additionalPlans.length);
    const newHasAdditional = !!(changedOrderItem && changedOrderItem.additionalPlans && changedOrderItem.additionalPlans.length);
    if (!oldHasAdditional && !newHasAdditional) {
      return false;
    }
    return PortalHelper.getAdditionalPlanSignature(oldOrderItem) !== PortalHelper.getAdditionalPlanSignature(changedOrderItem);
  }

  static enableChangeSub(changedOrderItem, oldSubData) {
    var oldOrderItem = oldSubData.orderItem
    var oldItems = [].concat(oldOrderItem.plan, oldOrderItem.addons);

    if (PortalHelper.hasAdditionalPlanChanges(oldOrderItem, changedOrderItem)) {
      return true;
    }

    // Compare addon IDs from existing sub (if any ) with the new addon IDs added to the cart
    
    const oldAddons = JSON.stringify(oldOrderItem.addons.map(addons => addons.id).sort());
    const newAddons = JSON.stringify(changedOrderItem.addons.map(addons => addons.id).sort());

      if(oldAddons !== newAddons){
        return true;
      }

    return oldItems.filter(item => {
      if(item.type == ItemType.PLAN) {
        return (item.id != changedOrderItem.plan.id) 
        || ((item.quantity_in_decimal != changedOrderItem.plan.quantity_in_decimal)
        || ((item.quantity || 1) != (changedOrderItem.plan.quantity || 1)));
      }
      if(item.type == ItemType.ADDON) {
        let t = changedOrderItem.addons.filter(i => i.id == item.id)[0];
        return (t && (t.quantity_in_decimal != item.quantity_in_decimal)) || (t && (t.quantity != item.quantity));
      }
    }).length > 0 || !!PortalHelper.hasCouponChanges(changedOrderItem.coupon_list , oldSubData.recurringEstimate && oldSubData.recurringEstimate.discounts);
  }

  static hasCouponChanges(newCouponList, oldDiscount) {
    return (newCouponList && oldDiscount) ? newCouponList.length != oldDiscount.length
              || newCouponList.filter(coupon => !oldDiscount.some(discount => discount.entity_id == coupon.id)).length > 0
              || oldDiscount.filter(discount => !newCouponList.some(coupon => coupon.id == discount.entity_id)).length > 0
          : (newCouponList && newCouponList.length > 0 && !oldDiscount) || (!newCouponList && oldDiscount && oldDiscount.length > 0);
  }

  static filterAddonsBasedOnPlanTerm(addons, plan, includeNonRecAddons) {
    var selected_addons = []
    switch(plan.period_unit) {
      case "week":
        selected_addons = addons.filter(a => a.period_unit == "week" && (plan.period % a.period) == 0);
        selected_addons = selected_addons.concat(addons.filter(a => a.period_unit == "day" && ((plan.period * 7) % a.period) == 0))
        break;
      case "month":
        selected_addons = addons.filter(a => a.period_unit == "month" && (plan.period % a.period) == 0);
        break;
      case "year":
        selected_addons = addons.filter(a => a.period_unit == "year" && (plan.period % a.period) == 0);
        selected_addons = selected_addons.concat(addons.filter(a => a.period_unit == "month" && ((plan.period * 12) % a.period) == 0))
        break;
      case "day":
        selected_addons = addons.filter(a => a.period_unit == "day" && (plan.period % a.period) == 0);
        break;
    }

    // Non-recurring addons
    if(includeNonRecAddons) { 
      selected_addons = selected_addons.concat(addons.filter(a => {
        return (!a.period && (!a.period_unit || a.period_unit == "not_applicable")) || a.item_type == ItemType.CHARGE
      }))
    }
    return selected_addons;
  }

  static canAccessAddPlanPage({ isMpsEnabled, allowAddPlans } = {}) {
    return !!isMpsEnabled && !!allowAddPlans;
  }

  static canShowAddPlanOption({ isMpsEnabled, allowAddPlans, isItemsModel } = {}) {
    return PortalHelper.canAccessAddPlanPage({ isMpsEnabled, allowAddPlans })
      && !!isItemsModel;
  }

  static sameBillingFrequency(itemPrice, plan) {
    const details = plan && (plan.details || plan);
    // Missing frequency on either side → treat as compatible (catalog stubs / legacy plans).
    if (!itemPrice || !details || details.period == null || !details.period_unit
      || itemPrice.period == null || !itemPrice.period_unit) {
      return true;
    }
    return Number(itemPrice.period) === Number(details.period)
      && String(itemPrice.period_unit || '').toLowerCase() === String(details.period_unit).toLowerCase();
  }

  static getAddablePlanItemPrices({
    orderItem,
    otherProductItems,
  } = {}) {
    if (!otherProductItems) {
      return [];
    }
    const cartPlans = HostedPageHelper.getAllPlans(orderItem);
    const existingPlanIds = cartPlans.map(plan => plan.id);
    const subscriptionPlan = cartPlans[0];
    const planItemIds = (otherProductItems.items || [])
      .filter(item => item && item.type == ItemType.PLAN && !item.is_giftable)
      .map(item => item.id);
    return (otherProductItems.item_prices || []).filter(itemPrice =>
      planItemIds.indexOf(itemPrice.item_id) > -1
      && existingPlanIds.indexOf(itemPrice.id) === -1
      && PortalHelper.sameBillingFrequency(itemPrice, subscriptionPlan)
    );
  }

  static getChangePlanItemPrices({
    itemPrices,
    orderItem,
    planIdBeingChanged,
    blockedPlanIds,
  } = {}) {
    if (!itemPrices || !itemPrices.length) {
      return itemPrices || [];
    }
    const cartPlans = HostedPageHelper.getAllPlans(orderItem);
    const subscriptionPlan = cartPlans.find((plan) => plan && plan.id === planIdBeingChanged) || cartPlans[0];
    const blockedIds = new Set(blockedPlanIds || []);
    if (!blockedIds.size) {
      cartPlans.forEach((plan) => {
        if (plan && plan.id) {
          blockedIds.add(plan.id);
        }
      });
      if (planIdBeingChanged) {
        blockedIds.add(planIdBeingChanged);
      }
    }
    if (!blockedIds.size) {
      return itemPrices.filter((itemPrice) =>
        PortalHelper.sameBillingFrequency(itemPrice, subscriptionPlan)
      );
    }
    const blockedItemIds = new Set();
    itemPrices.forEach((itemPrice) => {
      if (itemPrice && blockedIds.has(itemPrice.id) && itemPrice.item_id) {
        blockedItemIds.add(itemPrice.item_id);
      }
    });
    return itemPrices.filter((itemPrice) => {
      if (!itemPrice) {
        return false;
      }
      if (blockedIds.has(itemPrice.id)) {
        return false;
      }
      if (itemPrice.item_id && blockedItemIds.has(itemPrice.item_id)) {
        return false;
      }
      return PortalHelper.sameBillingFrequency(itemPrice, subscriptionPlan);
    });
  }

  static hasCompatibleChangePlanTargets({
    itemPrices,
    orderItem,
    planIdBeingChanged,
    catalogItems,
  } = {}) {
    let planPrices = itemPrices || [];
    if (catalogItems && catalogItems.length) {
      const planItemIds = {};
      catalogItems.forEach((item) => {
        if (item && item.type == ItemType.PLAN && item.id) {
          planItemIds[item.id] = true;
        }
      });
      planPrices = planPrices.filter(
        (price) => price && price.item_id && planItemIds[price.item_id]
      );
    }
    return PortalHelper.getChangePlanItemPrices({
      itemPrices: planPrices,
      orderItem,
      planIdBeingChanged,
    }).length > 0;
  }

  static buildItemFamilyMap(otherProductItems) {
    const map = {};
    ((otherProductItems && otherProductItems.items) || []).forEach((item) => {
      if (!item || !item.id) {
        return;
      }
      const family = item.item_family_id || item.family_code;
      if (family) {
        map[item.id] = family;
      }
    });
    return map;
  }

  static getCartEntityFamilyCode(entity, familyMap = {}) {
    if (!entity) {
      return null;
    }
    const itemId = entity.details && entity.details.item_id;
    if (itemId && familyMap[itemId]) {
      return familyMap[itemId];
    }
    return (entity.details
      && (entity.details.item_family_id || entity.details.family_code)) || null;
  }

  static getCartPlanFamilies(orderItem, familyMap = {}) {
    const planFamilies = new Set();
    if (!orderItem || !familyMap || !Object.keys(familyMap).length) {
      return planFamilies;
    }
    HostedPageHelper.getAllPlans(orderItem).forEach((plan) => {
      const family = PortalHelper.getCartEntityFamilyCode(plan, familyMap);
      if (family) {
        planFamilies.add(family);
      }
    });
    return planFamilies;
  }

  static isCatalogItemCompatibleWithCartFamilies(catalogItem, familyMap = {}, planFamilies) {
    if (!planFamilies || planFamilies.size === 0) {
      return true;
    }
    if (!catalogItem || !catalogItem.id) {
      return true;
    }
    const family = familyMap[catalogItem.id]
      || catalogItem.item_family_id
      || catalogItem.family_code;
    if (!family) {
      return true;
    }
    return planFamilies.has(family);
  }

  static getOrphanedAddonsByFamily({ orderItem, familyMap } = {}) {
    if (!orderItem || !familyMap || !Object.keys(familyMap).length) {
      return [];
    }
    const planFamilies = PortalHelper.getCartPlanFamilies(orderItem, familyMap);
    if (planFamilies.size === 0) {
      return [];
    }
    return (orderItem.addons || []).filter((addon) => {
      const family = PortalHelper.getCartEntityFamilyCode(addon, familyMap);
      return !!family && !planFamilies.has(family);
    });
  }

  static getAddableAddonIds({
    addonsMasterList = [],
    cartAddons = [],
    isItemsModel = false,
    plan,
    allItemPrices = [],
    allowRecurringAddons = false,
    allowNonRecAddons = false,
    orderItem,
    otherProductItems,
    isMpsFamilyRestrictionEnabled = false,
  } = {}) {
    const cartAddonIds = cartAddons.map(addon => addon.id);
    let addonIds = addonsMasterList.filter(id => cartAddonIds.indexOf(id) === -1);
    if (isItemsModel) {
      if (!plan) {
        return [];
      }
      let itemPrices = allItemPrices.filter(itemPrice => addonIds.indexOf(itemPrice.id) > -1);
      itemPrices = PortalHelper.filterAddonsBasedOnPlanTerm(itemPrices, plan, allowNonRecAddons);
      if (!allowRecurringAddons) {
        itemPrices = itemPrices.filter(itemPrice => (!itemPrice.period_unit || itemPrice.period_unit == "not_applicable"));
      }
      addonIds = itemPrices.map(itemPrice => itemPrice.id);
    }
    if (isMpsFamilyRestrictionEnabled && orderItem && otherProductItems) {
      const familyMap = PortalHelper.buildItemFamilyMap(otherProductItems);
      const planFamilies = PortalHelper.getCartPlanFamilies(orderItem, familyMap);
      if (planFamilies.size > 0) {
        const catalogItemsById = {};
        ((otherProductItems.items || [])).forEach((item) => {
          if (item && item.id) {
            catalogItemsById[item.id] = item;
          }
        });
        addonIds = addonIds.filter((addonPriceId) => {
          const itemPrice = allItemPrices.find((price) => price.id === addonPriceId);
          const catalogItem = itemPrice && catalogItemsById[itemPrice.item_id];
          if (catalogItem) {
            return PortalHelper.isCatalogItemCompatibleWithCartFamilies(
              catalogItem, familyMap, planFamilies
            );
          }
          const family = itemPrice && familyMap[itemPrice.item_id];
          return !family || planFamilies.has(family);
        });
      }
    }
    return addonIds;
  }

  static constructOrderItemForOtherProducts(other_product, subData, getters, options = {}) {
    const planIdToReplace = options.planIdToReplace;
    const currentCart = (getters && getters.order_items)
      || (subData && subData.orderItem);
    const isMps = !!(getters && getters.is_mps_enabled);
    const cartPlanCount = HostedPageHelper.getAllPlans(currentCart).length;
    const targetPlanId = planIdToReplace
      || (cartPlanCount > 1 && currentCart && currentCart.plan && currentCart.plan.id)
      || null;
    const useMpsPlanSwap = isMps
      && !!targetPlanId
      && !!(currentCart && currentCart.plan)
      && (!!planIdToReplace || cartPlanCount > 1);

    if (useMpsPlanSwap) {
      if (!other_product || !other_product.plan_id) {
        return currentCart;
      }
      return this.swapPlanInOrderItem(other_product, subData, getters, targetPlanId, currentCart);
    }

    if (!other_product || !other_product.plan_id) {
      return (getters && getters.order_items) || (subData && subData.orderItem);
    }

    var current_addons = this.cloneAddons(subData.orderItem.addons);
    
    // Check if the new plan supports quantity by looking up its pricing model
    const itemPriceList = (subData.other_product_items && subData.other_product_items.item_prices) || [];
    const newPlanItemPrice = itemPriceList.find(
      itemPrice => itemPrice.id === other_product.plan_id
    );
    const newPlanSupportsQuantity = newPlanItemPrice && newPlanItemPrice.pricing_model !== PRICING_MODEL.FLAT_FEE;
    
    // Persist current quantity if the new plan supports quantity
    const currentCartQuantity = getters.change_order_plan?.quantity;
    const originalQuantity = subData.orderItem.plan.quantity;
    const quantity = newPlanSupportsQuantity ? (currentCartQuantity || originalQuantity || 1) : 1;
    
    var orderItem = {
      plan: {
        id: other_product.plan_id,
        quantity: quantity,
        type: ItemType.PLAN
      }
    }
    if(subData.isMultiDecimal && newPlanSupportsQuantity) {
      // First try current cart state, then fall back to original subscription
      const currentCartQuantityInDecimal = getters.change_order_plan?.quantity_in_decimal;
      const originalQuantityInDecimal = subData.orderItem.plan.quantity_in_decimal;
      orderItem.plan.quantity_in_decimal = currentCartQuantityInDecimal || originalQuantityInDecimal || MultiDecimalHelper.getDefaultQuantity();
    }
    orderItem.plan.initialQuantity = MultiDecimalHelper.getInitialQuantity(orderItem.plan);

    if(other_product.plan_id == subData.orderItem.plan.id)  {
      orderItem.addons = [].concat(current_addons);
    } else {
      let addonsList = current_addons;
      if(other_product.is_addons_restricted) {
        addonsList = addonsList.filter(addon => !!other_product.allowed_addons.find(_addon => _addon.id === addon.id))
      }
      var selected_addons = [];
      switch(subData.retain_addons_for_change && other_product.period_unit) {
        case "week":
          var selected_addons = addonsList.filter(a => a.details.period_unit == "week" && (other_product.period % a.details.period) == 0);
          break;
        case "month":
          var selected_addons = addonsList.filter(a => a.details.period_unit == "month" && (other_product.period % a.details.period) == 0);
          break;
        case "year":
          var selected_addons = addonsList.filter(a => a.details.period_unit == "year" && (other_product.period % a.details.period) == 0);
          selected_addons = selected_addons.concat(addonsList.filter(a => a.details.period_unit == "month" && ((other_product.period * 12) % a.details.period) == 0))
          break;
        case "day":
          var selected_addons =  addonsList.filter(a => a.details.period_unit == "day" && (other_product.period % a.details.period) == 0);
          break;
      }

      // union of other_product.addons and selected_addons
      let o = [].concat(selected_addons);
      let tmp = selected_addons.map(({id}) => id);
      other_product.addons.forEach(a => {
        if(a.id && tmp.indexOf(a.id) == -1) {
          o.push(a);
        }
      });
      orderItem.addons = o;
    }

    const currentOrderItemAddons = (getters.order_items || {}).addons || [];
    orderItem.addons.forEach(addon => {
      // TODO - Remove this hack added as part of CHKOUTENGG-13846
      const currentAddon = currentOrderItemAddons.find(_addon => _addon.id === addon.id);
      if(currentAddon) {
        addon.added_type = currentAddon.added_type;
      }
      // --- CHKOUTENGG-13846

      addon.type = ItemType.ADDON;
      addon.quantity = addon.quantity || 1;
      if(subData.isMultiDecimal) {
        addon.quantity_in_decimal = MultiDecimalHelper.getQuantity(addon) || MultiDecimalHelper.getDefaultQuantity();
      }
      addon.initialQuantity = MultiDecimalHelper.getInitialQuantity(addon);
    });
    // orderItem.coupon = hostedPage.coupon;
    // orderItem.flattened = [].concat(orderItem.plan, orderItem.addons);
    return orderItem;
  }

  static swapPlanInOrderItem(other_product, subData, getters, planIdToReplace, currentCart) {
    if (!other_product || !other_product.plan_id || !currentCart || !currentCart.plan) {
      return currentCart;
    }
    const itemPriceList = (subData.other_product_items && subData.other_product_items.item_prices) || [];
    const newPlanItemPrice = itemPriceList.find(
      (itemPrice) => itemPrice.id === other_product.plan_id
    );
    const newPlanSupportsQuantity = newPlanItemPrice
      && newPlanItemPrice.pricing_model !== PRICING_MODEL.FLAT_FEE;

    const allPlans = HostedPageHelper.getAllPlans(currentCart);
    const planBeingReplaced = allPlans.find((plan) => plan.id === planIdToReplace) || currentCart.plan;

    const quantity = newPlanSupportsQuantity
      ? (planBeingReplaced.quantity || 1)
      : 1;

    const newPlan = {
      id: other_product.plan_id,
      quantity,
      type: ItemType.PLAN,
      details: newPlanItemPrice || planBeingReplaced.details,
    };

    if (subData.isMultiDecimal && newPlanSupportsQuantity) {
      newPlan.quantity_in_decimal = planBeingReplaced.quantity_in_decimal
        || MultiDecimalHelper.getDefaultQuantity();
    }
    newPlan.initialQuantity = MultiDecimalHelper.getInitialQuantity(newPlan);

    const hadMatch = allPlans.some((plan) => plan.id === planIdToReplace);
    const swappedPlans = hadMatch
      ? allPlans.map((plan) => (plan.id === planIdToReplace ? newPlan : this.clonePlan(plan)))
      : [newPlan].concat(allPlans.slice(1).map((plan) => this.clonePlan(plan)));

    const seen = new Set();
    const dedupedPlans = [];
    swappedPlans.forEach((plan) => {
      if (!plan || !plan.id || seen.has(plan.id)) {
        return;
      }
      seen.add(plan.id);
      dedupedPlans.push(plan);
    });

    const orderItem = {
      plan: dedupedPlans[0],
      additionalPlans: dedupedPlans.slice(1),
      addons: this.cloneAddons(currentCart.addons),
    };

    if (getters && getters.is_mps_family_restriction_enabled) {
      const familyMap = PortalHelper.buildItemFamilyMap(subData && subData.other_product_items);
      const orphaned = PortalHelper.getOrphanedAddonsByFamily({ orderItem, familyMap });
      if (orphaned.length) {
        const orphanedIds = new Set(orphaned.map((addon) => addon.id));
        orderItem.addons = orderItem.addons.filter((addon) => !orphanedIds.has(addon.id));
        orderItem.replace_addon_list = true;
      }
    }

    orderItem.addons.forEach((addon) => {
      addon.type = ItemType.ADDON;
      addon.quantity = addon.quantity || 1;
      if (subData.isMultiDecimal) {
        addon.quantity_in_decimal = MultiDecimalHelper.getQuantity(addon)
          || MultiDecimalHelper.getDefaultQuantity();
      }
      addon.initialQuantity = MultiDecimalHelper.getInitialQuantity(addon);
    });

    if (currentCart.coupon_list) {
      orderItem.coupon_list = JSON.parse(JSON.stringify(currentCart.coupon_list));
    }
    if (currentCart.replace_coupon_list) {
      orderItem.replace_coupon_list = true;
    }
    if (currentCart.replace_addon_list) {
      orderItem.replace_addon_list = true;
    }

    return orderItem;
  }

  static canShowBreakup(estimate) {
    return estimate && (this.hasPricingModelItem(estimate)
                || (estimate.taxes && estimate.taxes.length > 0)
                || (estimate.discounts && estimate.discounts.length > 0));
  }

  static hasPricingModelItem(estimate) {
    if(estimate.line_items && estimate.line_items.length > 0){
      return estimate.line_items.some(i => {
        switch(i.entity_type) {
          case ItemType.PLAN:
          case ItemType.ADDON:
          return i.pricing_model == "tiered";
        }
      });
    }
  }

  static getCancelReasonText(ref, subData) {
    let reasonValue = subCancelReasonCodeConfig.getLocalizedTextByCode(subData.cancel_reason_code)
    if(subData.isScheduledForCancellation()) {
      return ref.t("subscription.sch_cancel.reason_code.info", {
        reason: reasonValue, sub_cancel_reason_code: reasonValue 
      });
    }
    return ref.t("subscription.canceled.reason_code.info", {
      reason: reasonValue, sub_cancel_reason_code: reasonValue 
    });
  }

  static isAddonRemoved(oldSubData, changeOrderData) {
    const oldAddonIds = new Set((oldSubData.addons || []).map(addon => addon.id));
    const newAddonIds = new Set((changeOrderData.addons || []).map(addon => addon.id));
    for (const id of oldAddonIds) {
      if (!newAddonIds.has(id)) {
        return true;
      }
    }
    return false;
  }

  static buildCouponChangeList(oldList, newList) {
      const all = Array.from(new Set([...oldList, ...newList]));
      return all.reduce((acc, code) => {
        const inOld = oldList.includes(code);
        const inNew = newList.includes(code);
        if (inOld && inNew) acc.push(`${code} - No change`);
        else if (!inOld && inNew) acc.push(`${code} - Added`);
        else if (inOld && !inNew) acc.push(`${code} - Removed`);
        return acc;
      }, []);
  }

  static buildDetailedChangeList(oldList, newList) {
      const all = Array.from(new Set([...oldList, ...newList]));
      return all.map(id => {
        if (oldList.includes(id) && newList.includes(id)) return `${id} - No change`;
        if (!oldList.includes(id) && newList.includes(id)) return `${id} - Added`;
        if (oldList.includes(id) && !newList.includes(id)) return `${id} - Removed`;
      });
  }

  static getItemsByType(items, type) {
    return (items || []).filter(item => item.item_type === type).map(item => item.item_price_id)
  }

  static getAddonRemovalMailContent(oldSub, newSub, oldSubCoupons) {
    const today = new Date().toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});

    const oldPlans = this.getItemsByType(oldSub.subscription_items, ItemType.PLAN);
    const oldAddons = this.getItemsByType(oldSub.subscription_items, ItemType.ADDON);
    const oldCharges = this.getItemsByType(oldSub.subscription_items, 'charge');
    const oldCoupons = (oldSubCoupons || []).map(c => c.code);

    const newCoupons = (newSub.coupon_list || []).map(c => c.code);
    const newPlan = newSub.plan?.id;
    const newAddons = (newSub.addons || []).map(a => a.id);
    const newCharges = (newSub.event_based_addons || []).map(c => c.id);

    const addonChanges = this.buildDetailedChangeList(oldAddons, newAddons);
    const chargeChanges = this.buildDetailedChangeList(oldCharges, newCharges);
    const couponChanges = this.buildCouponChangeList(oldCoupons, newCoupons);

    let changeContent = {}

    if(oldPlans[0] !== newPlan) {
      changeContent.plan = [
        `Current Plan: ${oldPlans[0] || ''}`,
        `New Plan: ${newPlan || ''}`
      ]
    }
    else {
      changeContent.plan = [
        `${oldPlans[0]} - no change`
      ]
    }

    if (addonChanges.length > 0) {
      changeContent.addons = []
      addonChanges.forEach(change => changeContent.addons.push(change));
    }

    if (chargeChanges.length > 0) {
      changeContent.charges = []
      chargeChanges.forEach(change => changeContent.charges.push(change));
    }

    if (couponChanges.length > 0) {
      changeContent.coupons = []
      couponChanges.forEach(change => changeContent.coupons.push(change));
    }

    changeContent.change_date = today

    return JSON.stringify(changeContent)
  }
}
